import { DEFAULT_ACCOUNT_PAGE_CONFIG } from "@shopflo/ui/account-page";
import { BorderSides, CART_SCROLLABLE_ELEMENTS_TYPE } from "../types/merchantConfigTypes";
import { DEFAULT_PROGRESS_BAR } from "@shopflo/ui/progress-bar";

// Coupon Page Config Defaults
export const DEFAULT_COUPON_PAGE_CONFIG = {
  backgroundColor: "#FFFFFF",
  textColor: "",
  primaryColor: "",
  cardRadius: 6,
  filterRadius: 6,
  buttonRadius: 6,
  inputRadius: 10,
} as const;

// Discount Config Defaults
export const DEFAULT_DISCOUNT_CONFIG = {
  callout: {
    backgroundColor: "",
    infoColor: "",
    primaryColor: "",
    borderColor: "",
  },
  footer: {
    backgroundColor: "",
    infoColor: "",
    primaryColor: "",
  },
  input: {
    backgroundColor: "",
    borderColor: "",
    primaryColor: "",
  },
  button: {
    backgroundColor: "",
    textColor: "",
  },
  totalSavings: {
    primaryColor: "",
    infoColor: "",
    backgroundColor: "#FFFFFF",
  },
  cardRadius: 12,
  cardBorderWidth: 0,
  cardBorderSides: "all",
} as const;

export const PAYMENT_CARD_RADIUS_FALLBACK = 16;

// Payment Tile Config Defaults
export const DEFAULT_PAYMENT_TILE_CONFIG = {
  layout: "ICONS" as const,
  layoutUI: {
    paymentCard: {
      backgroundColor: "",
      borderColor: "",
      textColor: "",
      iconColor: "",
      subtitleColor: "",
      dividerColor: "",
      borderRadius: PAYMENT_CARD_RADIUS_FALLBACK,
    },
    offerBadge: {
      backgroundColor: "",
      textColor: "",
    },
    paymentTileGap: "",
  },
  modeDetails: [],
};

export const DEFAULT_DIALOG_CONFIG = {
  dialogs: [],
};

export const DEFAULT_ITEM_CARD_CONFIG = {
  align: "column",
} as const;

// Checkout Metadata Defaults
export const DEFAULT_CHECKOUT_UI_ATTRIBUTES = {
  couponPageConfig: DEFAULT_COUPON_PAGE_CONFIG,
  logoUrl: "",
  bannerConfig: [],
  paymentTileConfig: DEFAULT_PAYMENT_TILE_CONFIG,
  discountConfig: DEFAULT_DISCOUNT_CONFIG,
  dialogConfig: DEFAULT_DIALOG_CONFIG,
  itemCardConfig: DEFAULT_ITEM_CARD_CONFIG,
};

export const DEFAULT_CHECKOUT_METADATA = {
  metadataLoading: true,
  uiAttributes: DEFAULT_CHECKOUT_UI_ATTRIBUTES,
};

// Cart Metadata Defaults
export const DEFAULT_CART_PAGE_CONFIG = {
  backgroundColor: "#FFFFFF",
} as const;

export const DEFAULT_CART_HEADER_CONFIG = {
  backgroundColor: "#FFFFFF",
  textColor: "#000000",
} as const;

export const DEFAULT_CART_FOOTER_CONFIG = {
  backgroundColor: "#FFFFFF",
  textColor: "#000000",
  savingsColor: "#2C874A",
  showSavings: true,
} as const;

export const DEFAULT_ADDON_CONFIG = {
  backgroundColor: "#f2f2f3",
  textColor: "#3f3f46",
  buttonColor: "#3f3f46",
} as const;

export const DEFAULT_ORDER_SUMMARY_CONFIG = {
  backgroundColor: "#f4f4f5",
  borderColor: "#FFFFFF",
  offerBadgeColor: "#2C874A",
  textColor: "#000000",
  quantityEditorBackground: "#FFFFFF",
  quantityEditorText: "#4D4D4D",
  cardRadius: 12,
  cardBorderWidth: 0,
  cardBorderSides: "all",
} as const;

export const DEFAULT_UPSELL_CONFIG = {
  isEnabled: false,
  upsellTitle: "",
  discountedUpsellTitle: "",
  shouldOverrideDiscountedUpsell: false,
  backgroundColor: "",
  borderColor: "",
  textColor: "",
  buttonColor: "",
  offerBadgeColor: "",
  titleColor: "",
  cardRadius: 12,
  cardBorderWidth: 0,
  cardBorderSides: "all",
  clearUpsellItemsOnCartClose: false,
} as const;

export const DEFAULT_RECENTLY_VIEWED_CONFIG: {
  enabled: boolean;
  minItems: number;
  maxItems: number;
  page: Array<"EMPTY_CART" | "CART">;
  tabTitle: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  buttonColor: string;
  offerBadgeColor: string;
  titleColor: string;
  cardRadius: number;
  cardBorderWidth: number;
  cardBorderSides: BorderSides;
} = {
  enabled: false,
  minItems: 1,
  maxItems: 5,
  page: ["EMPTY_CART", "CART"],
  tabTitle: "",
  backgroundColor: "",
  borderColor: "",
  textColor: "",
  buttonColor: "",
  offerBadgeColor: "",
  titleColor: "",
  cardRadius: 12,
  cardBorderWidth: 0,
  cardBorderSides: "all",
};

export const DEFAULT_NOTES_CONFIG = {
  isEnabled: false,
  backgroundColor: "",
  borderColor: "",
  textColor: "",
  buttonColor: "",
  cardRadius: 12,
  cardBorderWidth: 0,
  cardBorderSides: "all",
} as const;

export const DEFAULT_CART_ELEMENT_ORDER: CART_SCROLLABLE_ELEMENTS_TYPE[] = [
  "ORDER_SUMMARY",
  "DISCOUNTS",
  "UPSELL",
  "NOTES",
];

export const DEFAULT_CART_UI_ATTRIBUTES = {
  bannerConfig: [],
  primaryCtaDisabilityRule: "",
  itemModificationRule: {},
  showUpiIconsOnCartButton: false,
  page: DEFAULT_CART_PAGE_CONFIG,
  header: DEFAULT_CART_HEADER_CONFIG,
  footer: DEFAULT_CART_FOOTER_CONFIG,
  addonConfig: DEFAULT_ADDON_CONFIG,
  orderSummaryConfig: DEFAULT_ORDER_SUMMARY_CONFIG,
  itemCardConfig: DEFAULT_ITEM_CARD_CONFIG,
  upsell: DEFAULT_UPSELL_CONFIG,
  recentlyViewedConfig: DEFAULT_RECENTLY_VIEWED_CONFIG,
  notesConfig: DEFAULT_NOTES_CONFIG,
  discountConfig: DEFAULT_DISCOUNT_CONFIG,
  elementOrder: DEFAULT_CART_ELEMENT_ORDER,
  couponPageConfig: DEFAULT_COUPON_PAGE_CONFIG,
  dialogConfig: DEFAULT_DIALOG_CONFIG,
};

export const DEFAULT_CART_METADATA = {
  metadataLoading: true,
  progressBarConfig: DEFAULT_PROGRESS_BAR,
  uiAttributes: DEFAULT_CART_UI_ATTRIBUTES,
};

// SSO Metadata Defaults
export const DEFAULT_SSO_PRIMARY_CTA = {
  text: "Continue",
  color: "#FFFFFF",
  background_color: "#000000",
} as const;

export const DEFAULT_SSO_TITLE = {
  text: "Sign in to unlock personalized benefits",
  color: "#E0E0E0",
} as const;

export const DEFAULT_SSO_BANNER = {
  enabled: false,
  background_color: "",
  text_color: "",
  items: [],
};

export const DEFAULT_SSO_CARD = {
  enabled: false,
  background_color: "",
  text_color: "",
  border_color: "",
  border_width: "",
  border_radius: "",
  items: [],
};

export const DEFAULT_SSO_POPUP = {
  logo_url: "",
  background_color: "#FFFFFF",
  pre_login_content_alignment: "center" as const,
  pop_trigger_time: 10000,
  border_radius: "8px",
  primary_cta: DEFAULT_SSO_PRIMARY_CTA,
  title: DEFAULT_SSO_TITLE,
  banner: DEFAULT_SSO_BANNER,
  card: DEFAULT_SSO_CARD,
};

export const DEFAULT_SSO_METADATA = {
  metadataLoading: true,
  popup: DEFAULT_SSO_POPUP,
  layout: {
    enable_marketing_flag: true,
  },
  accounts: DEFAULT_ACCOUNT_PAGE_CONFIG,
};

// Merchant Initial State
export const DEFAULT_MERCHANT_STATE = {
  accountId: null,
  isValidating: true,
  addressConfig: [],
  checkoutMetadata: DEFAULT_CHECKOUT_METADATA,
  cartMetadata: DEFAULT_CART_METADATA,
  ssoMetadata: DEFAULT_SSO_METADATA,
  consentState: { isConsentEnabled: false },
};
